import { useState, useEffect } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useReadContract } from 'wagmi';
import { formatUnits } from 'viem';
import { CONTRACT_ADDRESSES, LP_REWARDS_ABI, PAIR_ABI } from '../config/contracts';

function LPRewardsPanel({ pairAddress, tokenA, tokenB }) {
  const { address } = useAccount();
  const [pendingRewards, setPendingRewards] = useState({ token0: '0', token1: '0' });
  const [status, setStatus] = useState('');
  const [autoCompoundEnabled, setAutoCompoundEnabled] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const { writeContract, data: txHash, isPending } = useWriteContract();
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash: txHash });

  // Get pending rewards
  const { data: pendingRewardsData, refetch: refetchRewards } = useReadContract({
    address: CONTRACT_ADDRESSES.LP_REWARDS,
    abi: LP_REWARDS_ABI,
    functionName: 'getPendingRewards',
    args: (pairAddress && address) ? [pairAddress, address] : undefined,
    query: {
      enabled: !!(pairAddress && address && CONTRACT_ADDRESSES.LP_REWARDS),
      retry: true,
      refetchInterval: 10000, // Refetch every 10 seconds
    },
  });

  // Get token info from pair
  const { data: token0Address } = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'token0',
    query: {
      enabled: !!pairAddress,
    },
  });

  const { data: token1Address } = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'token1',
    query: {
      enabled: !!pairAddress,
    },
  });

  // Get total pool fees
  const { data: poolFeesData } = useReadContract({
    address: CONTRACT_ADDRESSES.LP_REWARDS,
    abi: LP_REWARDS_ABI,
    functionName: 'getPoolFees',
    args: pairAddress ? [pairAddress] : undefined,
    query: {
      enabled: !!(pairAddress && CONTRACT_ADDRESSES.LP_REWARDS),
    },
  });

  // Get auto-compound status
  const { data: autoCompoundStatus, refetch: refetchAutoCompound } = useReadContract({
    address: CONTRACT_ADDRESSES.LP_REWARDS,
    abi: LP_REWARDS_ABI,
    functionName: 'isAutoCompoundEnabled',
    args: (pairAddress && address) ? [pairAddress, address] : undefined,
    query: {
      enabled: !!(pairAddress && address && CONTRACT_ADDRESSES.LP_REWARDS),
    },
  });

  useEffect(() => {
    if (pendingRewardsData && Array.isArray(pendingRewardsData)) {
      const [token0Pending, token1Pending] = pendingRewardsData;
      setPendingRewards({
        token0: formatUnits(token0Pending || 0n, 18),
        token1: formatUnits(token1Pending || 0n, 18),
      });
    }
  }, [pendingRewardsData]);

  useEffect(() => {
    if (autoCompoundStatus !== undefined) {
      setAutoCompoundEnabled(autoCompoundStatus);
    }
  }, [autoCompoundStatus]);

  useEffect(() => {
    if (isSuccess) {
      if (status.includes('Auto-compound')) {
        setStatus('✅ Rewards auto-compounded successfully!');
      } else if (status.includes('Auto-compound setting')) {
        setStatus(`✅ Auto-compound ${autoCompoundEnabled ? 'enabled' : 'disabled'}!`);
        refetchAutoCompound();
      } else {
        setStatus('✅ Rewards claimed successfully!');
      }
      refetchRewards();
      setTimeout(() => setStatus(''), 5000);
    }
  }, [isSuccess, refetchRewards, refetchAutoCompound, status, autoCompoundEnabled]);

  const handleClaimRewards = async () => {
    if (!pairAddress || !CONTRACT_ADDRESSES.LP_REWARDS) {
      setStatus('❌ LP Rewards not available');
      return;
    }

    const hasRewards = parseFloat(pendingRewards.token0) > 0 || parseFloat(pendingRewards.token1) > 0;
    if (!hasRewards) {
      setStatus('ℹ️ No rewards to claim');
      return;
    }

    try {
      setStatus('⏳ Claiming rewards...');
      await writeContract({
        address: CONTRACT_ADDRESSES.LP_REWARDS,
        abi: LP_REWARDS_ABI,
        functionName: 'claimRewards',
        args: [pairAddress],
      });
    } catch (error) {
      console.error('Claim error:', error);
      setStatus(`❌ ${error.message?.split('(')[0] || 'Claim failed'}`);
      setTimeout(() => setStatus(''), 5000);
    }
  };

  const handleToggleAutoCompound = async () => {
    if (!pairAddress || !CONTRACT_ADDRESSES.LP_REWARDS) {
      setStatus('❌ LP Rewards not available');
      return;
    }

    try {
      setStatus('⏳ Updating auto-compound setting...');
      await writeContract({
        address: CONTRACT_ADDRESSES.LP_REWARDS,
        abi: LP_REWARDS_ABI,
        functionName: 'setAutoCompound',
        args: [pairAddress, !autoCompoundEnabled],
      });
    } catch (error) {
      console.error('Toggle auto-compound error:', error);
      setStatus(`❌ ${error.message?.split('(')[0] || 'Update failed'}`);
      setTimeout(() => setStatus(''), 5000);
    }
  };

  const handleAutoCompound = async () => {
    if (!pairAddress || !CONTRACT_ADDRESSES.LP_REWARDS) {
      setStatus('❌ LP Rewards not available');
      return;
    }

    const hasRewards = parseFloat(pendingRewards.token0) > 0 && parseFloat(pendingRewards.token1) > 0;
    if (!hasRewards) {
      setStatus('ℹ️ Need both token rewards to compound');
      return;
    }

    try {
      setStatus('⏳ Auto-compounding rewards...');
      const deadline = Math.floor(Date.now() / 1000) + 60 * 20; // 20 minutes
      await writeContract({
        address: CONTRACT_ADDRESSES.LP_REWARDS,
        abi: LP_REWARDS_ABI,
        functionName: 'autoCompound',
        args: [
          pairAddress,
          0n, // amountAMin (can be adjusted for slippage tolerance)
          0n, // amountBMin
          deadline
        ],
      });
    } catch (error) {
      console.error('Auto-compound error:', error);
      setStatus(`❌ ${error.message?.split('(')[0] || 'Auto-compound failed'}`);
      setTimeout(() => setStatus(''), 5000);
    }
  };

  const hasRewards = parseFloat(pendingRewards.token0) > 0 || parseFloat(pendingRewards.token1) > 0;
  const totalPoolToken0Fees = poolFeesData ? formatUnits(poolFeesData[0] || 0n, 18) : '0';
  const totalPoolToken1Fees = poolFeesData ? formatUnits(poolFeesData[1] || 0n, 18) : '0';

  if (!CONTRACT_ADDRESSES.LP_REWARDS) {
    return (
      <div className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border border-orange-500/20 rounded-xl p-6 mt-4">
        <div
          className="flex items-center justify-between cursor-pointer select-none"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">💰</span>
            <h3 className="text-base font-bold text-white">LP Rewards</h3>
            <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded-full ml-2">
              Earn on swaps
            </span>
          </div>
          <svg
            width="16"
            height="16"
            className={`text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        {isExpanded && (
          <p className="text-gray-400 text-sm mt-3">LP Rewards system not deployed yet</p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border border-orange-500/20 rounded-xl p-6 mt-4">
      <div
        className="flex items-center justify-between cursor-pointer select-none mb-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">💰</span>
          <h3 className="text-base font-bold text-white">LP Rewards</h3>
          <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded-full ml-2">
            Earn on swaps
          </span>
        </div>
        <svg
          width="16"
          height="16"
          className={`text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isExpanded && (
        <>
          {/* Pending Rewards */}
          <div className="bg-black/20 rounded-lg p-4 mb-4">
        <h4 className="text-sm font-semibold text-gray-300 mb-3">Your Pending Rewards</h4>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-400">
              {tokenA?.symbol || 'Token 0'}
            </span>
            <span className="text-lg font-mono font-semibold text-orange-400">
              {parseFloat(pendingRewards.token0).toFixed(6)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-400">
              {tokenB?.symbol || 'Token 1'}
            </span>
            <span className="text-lg font-mono font-semibold text-orange-400">
              {parseFloat(pendingRewards.token1).toFixed(6)}
            </span>
          </div>
        </div>
      </div>

      {/* Total Pool Fees */}
      <div className="bg-black/20 rounded-lg p-4 mb-4">
        <h4 className="text-sm font-semibold text-gray-300 mb-3">Total Pool Fees Collected</h4>
        <div className="space-y-2">
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-400">{tokenA?.symbol || 'Token 0'}</span>
            <span className="text-gray-300 font-mono">{parseFloat(totalPoolToken0Fees).toFixed(4)}</span>
          </div>
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-400">{tokenB?.symbol || 'Token 1'}</span>
            <span className="text-gray-300 font-mono">{parseFloat(totalPoolToken1Fees).toFixed(4)}</span>
          </div>
        </div>
      </div>

      {/* Auto-Compound Toggle */}
      <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-purple-400 text-lg">🔄</span>
            <h4 className="text-sm font-semibold text-purple-300">Auto-Compound</h4>
          </div>
          <button
            onClick={handleToggleAutoCompound}
            disabled={isPending || isConfirming || !address}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
              ${autoCompoundEnabled ? 'bg-purple-500' : 'bg-gray-600'}
              ${(isPending || isConfirming || !address) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                ${autoCompoundEnabled ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
        <p className="text-xs text-purple-200">
          {autoCompoundEnabled
            ? 'Rewards will be converted to LP tokens when you compound'
            : 'Enable to compound rewards into more LP tokens'}
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2">
          <span className="text-blue-400 text-sm">ℹ️</span>
          <p className="text-xs text-blue-200">
            You earn 0.25% of every swap in this pool, proportional to your LP share.
            Rewards accrue automatically and can be claimed or compounded anytime.
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        {/* Claim Button */}
        <button
          onClick={handleClaimRewards}
          disabled={!hasRewards || isPending || isConfirming || !address}
          className={`
            py-3 px-4 rounded-lg font-semibold transition-all
            ${hasRewards && address
              ? 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white'
              : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }
            ${(isPending || isConfirming) ? 'opacity-70 cursor-wait' : ''}
          `}
        >
          {isPending || isConfirming ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </span>
          ) : (
            'Claim'
          )}
        </button>

        {/* Auto-Compound Button */}
        <button
          onClick={handleAutoCompound}
          disabled={!(parseFloat(pendingRewards.token0) > 0 && parseFloat(pendingRewards.token1) > 0) || isPending || isConfirming || !address}
          className={`
            py-3 px-4 rounded-lg font-semibold transition-all
            ${(parseFloat(pendingRewards.token0) > 0 && parseFloat(pendingRewards.token1) > 0) && address
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white'
              : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }
            ${(isPending || isConfirming) ? 'opacity-70 cursor-wait' : ''}
          `}
        >
          {isPending || isConfirming ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </span>
          ) : (
            'Compound'
          )}
        </button>
      </div>

          {/* Status Message */}
          {status && (
            <div className={`
              mt-3 p-3 rounded-lg text-sm text-center
              ${status.includes('✅') ? 'bg-green-500/20 text-green-300 border border-green-500/30' : ''}
              ${status.includes('❌') ? 'bg-red-500/20 text-red-300 border border-red-500/30' : ''}
              ${status.includes('⏳') ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : ''}
              ${status.includes('ℹ️') ? 'bg-gray-500/20 text-gray-300 border border-gray-500/30' : ''}
            `}>
              {status}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default LPRewardsPanel;
